import { AlertTriangle } from "@untitledui/icons";
import { Button } from "@/components/base/buttons/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

type Blocker = {
  status: string;
  reset?: () => void;
  proceed?: () => void;
};

/** Discard/Keep editing/Save-and-leave variant of the unsaved-changes guard, for pages
 *  whose "Save" action can run from inside the dialog itself. `onSave` should resolve to
 *  whether the save succeeded, so a failed save keeps the dialog open instead of leaving. */
export function UnsavedChangesDialog({
  blocker,
  onSave,
  isSaving,
}: {
  blocker: Blocker;
  onSave: () => Promise<boolean>;
  isSaving?: boolean;
}) {
  return (
    <Dialog open={blocker.status === "blocked"} onOpenChange={() => blocker.status === "blocked" && blocker.reset?.()}>
      <DialogContent className="bg-unbox-cream-50">
        <DialogHeader className="gap-2">
          <div className="flex items-center gap-2.5">
            <AlertTriangle className="size-6 shrink-0 text-warning-500" aria-hidden />
            <DialogTitle className="text-md font-semibold text-primary">You have unsaved changes</DialogTitle>
          </div>
          <DialogDescription>Save your changes to this merchant, or discard them and leave.</DialogDescription>
        </DialogHeader>
        <DialogFooter className="sm:items-center sm:justify-between">
          <Button color="link-gray" size="sm" onClick={() => blocker.status === "blocked" && blocker.proceed?.()}>
            Discard
          </Button>
          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center">
            <Button color="secondary" size="sm" onClick={() => blocker.status === "blocked" && blocker.reset?.()}>
              Keep editing
            </Button>
            <Button
              color="primary"
              size="sm"
              isLoading={isSaving}
              onClick={async () => {
                const saved = await onSave();
                if (saved && blocker.status === "blocked") blocker.proceed?.();
              }}
            >
              Save and leave
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
